/**
 * AdBanner component — shows a banner ad on native platforms.
 * Uses BannerAdPluginEvents.SizeChanged to apply padding-top to document.documentElement,
 * pushing WebView content below the native banner overlay.
 * Hides when TopBar dropdowns are open. Resets padding on hide/remove.
 */

import { useEffect, useState, useRef } from "react";
import { shouldShowAds } from "../utils/ads";
import { useAuth } from "../contexts/AuthContext";
import { getBannerId, getPlatform } from "../utils/adConfig";
import { waitForAdMob } from "../utils/adMobState";

// Global visibility callback for dropdowns/modals
let adBannerVisibilityCallback = null;

export const setAdBannerVisible = (visible) => {
  if (adBannerVisibilityCallback) {
    adBannerVisibilityCallback(visible);
  }
};

// Track last known banner height for show/hide cycling
let lastBannerHeight = 0;

function applyBannerPadding(height) {
  lastBannerHeight = height;
  document.documentElement.style.paddingTop = `${height}px`;
}

function resetBannerPadding() {
  document.documentElement.style.paddingTop = "0px";
}

export default function AdBanner() {
  const { dbUser } = useAuth();
  const [isNative, setIsNative] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const adMobRef = useRef(null);
  const bannerActiveRef = useRef(false);
  const sizeListenerRef = useRef(null);
  const platformRef = useRef("web");

  // Register visibility callback
  useEffect(() => {
    adBannerVisibilityCallback = setIsVisible;
    return () => { adBannerVisibilityCallback = null; };
  }, []);

  // Check if native platform
  useEffect(() => {
    (async () => {
      const platform = await getPlatform();
      platformRef.current = platform;
      setIsNative(platform !== "web");
    })();
  }, []);

  // CRITICAL: Remove banner for Pro users immediately
  useEffect(() => {
    if (!isNative) return;

    const isPro = !shouldShowAds(dbUser);

    if (isPro) {
      (async () => {
        try {
          const { AdMob } = await import("@capacitor-community/admob");
          await AdMob.removeBanner();
          bannerActiveRef.current = false;
          resetBannerPadding();
          console.log("AdBanner: Removed for Pro user");
        } catch (e) {
          // Ignore - no banner to remove
        }
      })();
    }
  }, [isNative, dbUser]);

  // Show banner for free users only
  useEffect(() => {
    if (!isNative || !shouldShowAds(dbUser)) {
      return;
    }

    let cleanup = () => {};

    (async () => {
      try {
        const { AdMob, BannerAdSize, BannerAdPosition, BannerAdPluginEvents } = await import("@capacitor-community/admob");
        adMobRef.current = AdMob;

        if (!isVisible) {
          console.log("AdBanner: Skipping show — modal is open");
          bannerActiveRef.current = false;
          return;
        }

        const status = await waitForAdMob();
        if (status.failed) {
          console.log("AdBanner: AdMob initialization failed, skipping banner");
          return;
        }

        // Listen for SizeChanged to get actual banner height and apply padding
        sizeListenerRef.current = await AdMob.addListener(
          BannerAdPluginEvents.SizeChanged,
          (size) => {
            if (size && size.height > 0) {
              console.log("AdBanner: SizeChanged height =", size.height);
              applyBannerPadding(size.height);
            }
          }
        );

        const bannerId = await getBannerId();
        const platform = platformRef.current;

        // iOS: margin = status bar height only (to clear notch/Dynamic Island)
        // WebView content is pushed down via padding-top from SizeChanged event
        // Android: keep existing dynamic margin (statusBar + TopBar)
        let topMargin = 0;
        if (platform === "ios") {
          try {
            const { StatusBar } = await import("@capacitor/status-bar");
            const info = await StatusBar.getInfo();
            topMargin = info?.height || 47;
          } catch {
            topMargin = 47;
          }
        } else {
          try {
            const { StatusBar } = await import("@capacitor/status-bar");
            const info = await StatusBar.getInfo();
            const statusBarHeight = info?.height || 24;
            topMargin = Math.round(statusBarHeight + 56);
          } catch {
            topMargin = 100;
          }
        }

        await AdMob.showBanner({
          adId: bannerId,
          adSize: BannerAdSize.ADAPTIVE_BANNER,
          position: BannerAdPosition.TOP_CENTER,
          margin: topMargin,
        });

        bannerActiveRef.current = true;
        console.log("AdBanner: Showing for free user, margin:", topMargin);

        cleanup = async () => {
          try {
            if (sizeListenerRef.current) {
              await sizeListenerRef.current.remove();
              sizeListenerRef.current = null;
            }
            await AdMob.removeBanner();
            bannerActiveRef.current = false;
            resetBannerPadding();
          } catch {}
        };
      } catch (err) {
        console.error("AdBanner error:", err);
      }
    })();

    return () => { cleanup(); };
  }, [isNative, dbUser]);

  // Toggle visibility for dropdowns/modals
  useEffect(() => {
    if (!isNative || !adMobRef.current) return;
    if (!shouldShowAds(dbUser)) return;

    (async () => {
      try {
        if (isVisible) {
          if (bannerActiveRef.current) {
            await adMobRef.current.resumeBanner();
            // Reapply last known padding
            if (lastBannerHeight > 0) {
              applyBannerPadding(lastBannerHeight);
            }
          }
        } else {
          if (bannerActiveRef.current) {
            await adMobRef.current.hideBanner();
            resetBannerPadding();
          }
        }
      } catch (err) {
        console.log("AdBanner toggle error:", err);
      }
    })();
  }, [isVisible, isNative, dbUser]);

  return null;
}
